import { useCallback, useEffect, useRef } from 'react';
import { API_CALL_RESPONSE, API_SERVICES, type ApiService } from '@/consts/events';
import { trackApiResponse } from '@/utils/trackApiResponse';
import {
  MAX_TRANSIENT_POLLS,
  POLL_INTERVAL_MS,
  POLL_TIMEOUT_MS,
} from '@/consts/eligibilityPolling';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRecaptchaMint } from '@/hooks/useRecaptchaMint';
import { cmsApiClient } from '@/services/cmsApiClient';
import type {
  ACCESSAlignmentResultCode,
  AlignmentOutcomeCategory,
  TrackType,
} from '@/types/registration';
import { buildHypertensionOnlyConditions } from '@/utils/alignmentConditions';
import { isoBirthDateIsInRange, toIsoBirthDate } from '@/utils/date';
import { normalizeMbi } from '@/utils/mbi';
import { normalizeName } from '@/utils/name';
import type { WregIntakeForm } from './screens/wregIntakeFields';
import { WREG_STEP_NAME } from './wregSteps';
import { buildCmsIdentity, TERMINAL_POLL_STATUSES, errStatus, pollErrStatus, sleep } from './wregCmsPoll';

const WREG_TRACK: TrackType = 'eCKM';

const SUBMISSION_GONE_STATUS = 404;

export type WregAlignFailure = 'invalid-dob' | 'rejected' | 'failed' | 'cancelled';

export type WregAlignOutcome =
  | {
      ok: true;
      submissionId: string;
      outcome?: AlignmentOutcomeCategory;
      resultCode?: ACCESSAlignmentResultCode;
    }
  | { ok: false; reason: WregAlignFailure; submitted: boolean; status?: string | number };

type WregAlignSubmission = { ok: true; submissionId: string } | { ok: false; reason: WregAlignFailure };

export const wregAlignIdentity = (form: WregIntakeForm, switchAttested = false) =>
  [
    normalizeMbi(form.mbi),
    form.dob,
    normalizeName(form.first).toLowerCase(),
    normalizeName(form.last).toLowerCase(),
    form.gender.trim().toLowerCase(),
    switchAttested ? 'switch' : 'plain',
  ].join('|');

export const useWregAlignment = () => {
  const { trackError, track } = useAnalytics();
  // See useWregEligibility: the second condition on per-MBI sandbox routing, so a named test MBI
  // only enrols against the CMS sandbox when the registration came in on a test promo code.
  const { registrationData } = useRegistration();
  const promoCode = registrationData.promoCodeData?.code ?? '';
  const mintToken = useRecaptchaMint('wreg_alignment');
  const cancelledRef = useRef(false);
  const inFlightRef = useRef<{ identity: string; promise: Promise<WregAlignOutcome> } | null>(null);
  const submissionRef = useRef<{ identity: string; submissionId: string } | null>(null);
  const failureStatusRef = useRef<string | number | undefined>(undefined);

  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  const reportFailure = useCallback(
    (service: ApiService, status: string | number) => {
      failureStatusRef.current = status;
      trackError({
        event: API_CALL_RESPONSE,
        properties: { service, status, step_name: WREG_STEP_NAME.monitor },
      });
    },
    [trackError],
  );

  const reportSuccess = useCallback(() => {
    trackApiResponse(track, API_SERVICES.CMS_ALIGNMENT, WREG_STEP_NAME.monitor);
  }, [track]);

  const openSubmission = useCallback(
    async (
      form: WregIntakeForm,
      switchAttested: boolean,
      birthDate: string,
      identity: string,
    ): Promise<WregAlignSubmission> => {
      const open = submissionRef.current;
      if (open?.identity === identity) return { ok: true, submissionId: open.submissionId };
      try {
        const payload = {
          track: WREG_TRACK,
          ...buildCmsIdentity(form, birthDate),
          conditions: buildHypertensionOnlyConditions(),
          consentVoluntary: true,
          consentApiDataSharing: true,
          consentPcpSharing: true,
          consentControlGroup: true,
          consentTimestamp: Date.now(),
          promoCode,
        };
        const recaptchaToken = await mintToken(
          switchAttested ? 'wreg_alignment_switch' : 'wreg_alignment',
        );
        const submitted = switchAttested
          ? await cmsApiClient.postAlignmentSwitch(
              { ...payload, switchConsentAttestation: true },
              undefined,
              recaptchaToken,
            )
          : await cmsApiClient.postAlignment(payload, undefined, recaptchaToken);
        if (!submitted.submissionId) {
          reportFailure(API_SERVICES.CMS_ALIGNMENT, 'no-submission-id');
          return { ok: false, reason: 'failed' };
        }
        submissionRef.current = { identity, submissionId: submitted.submissionId };
        reportSuccess();
        return { ok: true, submissionId: submitted.submissionId };
      } catch (err) {
        const status = errStatus(err);
        reportFailure(API_SERVICES.CMS_ALIGNMENT, status);
        return { ok: false, reason: status === 422 ? 'rejected' : 'failed' };
      }
    },
    [mintToken, promoCode, reportFailure, reportSuccess],
  );

  const forgetSubmission = useCallback((identity: string) => {
    if (submissionRef.current?.identity === identity) submissionRef.current = null;
  }, []);

  const run = useCallback(
    async (
      form: WregIntakeForm,
      switchAttested: boolean,
      identity: string,
    ): Promise<WregAlignOutcome> => {
      const birthDate = toIsoBirthDate(form.dob);
      if (!isoBirthDateIsInRange(birthDate)) {
        return { ok: false, reason: 'invalid-dob', submitted: false };
      }

      cancelledRef.current = false;
      const opened = await openSubmission(form, switchAttested, birthDate, identity);
      if (!opened.ok) return { ok: false, reason: opened.reason, submitted: false };
      const { submissionId } = opened;

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let transientReported = false;
      let transientRun = 0;
      while (Date.now() < deadline) {
        if (cancelledRef.current) return { ok: false, reason: 'cancelled', submitted: true };
        try {
          const result = await cmsApiClient.getAlignmentResult(submissionId);
          transientRun = 0;
          if (result.status === 'complete') {
            return { ok: true, submissionId, outcome: result.outcome, resultCode: result.resultCode };
          }
          if (result.status === 'error' || result.status === 'timeout') {
            reportFailure(API_SERVICES.CMS_ALIGNMENT_POLL, result.status);
            return { ok: false, reason: 'failed', submitted: true };
          }
        } catch (err) {
          const status = pollErrStatus(err);
          if (status === SUBMISSION_GONE_STATUS) {
            forgetSubmission(identity);
            reportFailure(API_SERVICES.CMS_ALIGNMENT_POLL, status);
            return { ok: false, reason: 'failed', submitted: false };
          }
          if (status !== undefined && TERMINAL_POLL_STATUSES.has(status)) {
            reportFailure(API_SERVICES.CMS_ALIGNMENT_POLL, status);
            return { ok: false, reason: 'failed', submitted: true };
          }
          if (!transientReported) {
            transientReported = true;
            reportFailure(API_SERVICES.CMS_ALIGNMENT_POLL, status ?? 'unknown');
          }
          transientRun += 1;
          if (transientRun >= MAX_TRANSIENT_POLLS) {
            reportFailure(API_SERVICES.CMS_ALIGNMENT_POLL, 'transient-exhausted');
            return { ok: false, reason: 'failed', submitted: true };
          }
        }
        await sleep(POLL_INTERVAL_MS);
      }
      reportFailure(API_SERVICES.CMS_ALIGNMENT_POLL, 'timeout');
      return { ok: false, reason: 'failed', submitted: true };
    },
    [forgetSubmission, openSubmission, reportFailure],
  );

  const submit = useCallback(
    (form: WregIntakeForm, switchAttested = false): Promise<WregAlignOutcome> => {
      const identity = wregAlignIdentity(form, switchAttested);
      const inFlight = inFlightRef.current;
      if (inFlight && inFlight.identity === identity) return inFlight.promise;
      failureStatusRef.current = undefined;
      const promise = run(form, switchAttested, identity)
        .then((outcome): WregAlignOutcome =>
          outcome.ok ? outcome : { ...outcome, status: failureStatusRef.current },
        )
        .finally(() => {
        if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
      });
      inFlightRef.current = { identity, promise };
      return promise;
    },
    [run],
  );

  return { submit };
};
